import type { LanguageCodes } from "../../../types";
import { checkIpi, checkIsrc, checkIswc, IdentifierCheck } from "./identifiers";
import { messageFor } from "./identifiers/identifierMessages";

/**
 * Lo que el cliente agrega a las reglas copiadas del server (./identifiers, con sus
 * textos en castellano, inglés y portugués en identifierMessages.ts):
 *   - el chequeo de guardado: error si el valor está mal o no está en su forma
 *     normalizada (el server rechaza las dos cosas), aviso si la regla avisa;
 *   - "¿cambió el campo?", contra el snapshot original del update store.
 *
 * Los textos de los errores y del aviso son los de messageFor: los mismos que da el
 * server cuando rechaza el valor.
 */
export type IdentifierKind = "isrc" | "iswc" | "ipi";

export type IdentifierFeedback = { error?: string; warning?: string };

const CHECKS: Record<IdentifierKind, (raw: string | null | undefined) => IdentifierCheck> = {
  isrc: checkIsrc,
  iswc: checkIswc,
  ipi: checkIpi,
};

export const checkIdentifier = (
  kind: IdentifierKind,
  raw: string | null | undefined
): IdentifierCheck => CHECKS[kind](raw);

const notNormalizedText = (normalized: string | null, language: LanguageCodes): string => {
  if (normalized === null) {
    return {
      es: "Tiene solo espacios: borralos para dejar el campo vacío.",
      en: "It only has spaces: delete them to leave the field empty.",
      pt: "Tem apenas espaços: apague-os para deixar o campo vazio.",
    }[language];
  }
  return {
    es: `Corregí el formato: se guarda como ${normalized}.`,
    en: `Fix the format: it is stored as ${normalized}.`,
    pt: `Corrija o formato: é armazenado como ${normalized}.`,
  }[language];
};

/**
 * Chequeo de guardado. Error si la regla da error o si el valor no está en su forma
 * normalizada (lo que pasa cuando no se salió del campo, por ejemplo al editar el ISRC
 * en la grilla de fonogramas); aviso si la regla avisa. Vacío: sin nada.
 */
export function identifierFeedback(
  kind: IdentifierKind,
  current: string | null | undefined,
  language: LanguageCodes
): IdentifierFeedback {
  const raw = current ?? "";
  const check = checkIdentifier(kind, raw);
  if (check.error) return { error: messageFor(check.error, language) };
  if ((check.value ?? "") !== raw) return { error: notNormalizedText(check.value, language) };
  if (check.warning) return { warning: messageFor(check.warning, language) };
  return {};
}

const asText = (value: unknown): string => (typeof value === "string" ? value : "");

/**
 * ¿Cambió el campo? Sin `original` (create) cuenta como cambiado. En update se compara
 * contra el snapshot que arma el store al cargar (o al abrir el popup de un fonograma
 * dentro de la obra); vacío, null y undefined son lo mismo.
 */
export function identifierChanged(
  current: string | null | undefined,
  original: Record<string, unknown> | undefined,
  field: string
): boolean {
  if (!original) return true;
  return asText(current) !== asText(original[field]);
}
